import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Button, Box, CircularProgress, Divider } from "@mui/material";
import StoryCard from "../components/StoryCard";
import { fetchStoriesForEditor } from "../utils/api";
import useAuth from "../hooks/useAuth";

const EditorialPage = () => {
    const navigate = useNavigate();
    const { user, customClaims, loading } = useAuth();
    const [stories, setStories] = useState({
        contracted: [],
        userStories: [],
        flagged: []
    });
    const [loadingStories, setLoadingStories] = useState(true);

    const hasFetchedRef = useRef(false); // 🔹 Prevents multiple fetches
    const isEditor = useMemo(() => !!customClaims?.isEditor, [customClaims]); // 🔹 Ensures proper boolean evaluation

    // 🔹 useCallback prevents unnecessary re-creation of fetchStories
    const fetchStories = useCallback(async () => {
        if (hasFetchedRef.current) return; // Prevent double fetching
        hasFetchedRef.current = true; // Mark as fetched
        
        try {
            const [contracted, userWritten, flagged] = await Promise.all([
                fetchStoriesForEditor({ isPublished: true, isUGC: "missing", limit: 10, order: "desc" }),
                fetchStoriesForEditor({ isUGC: true, limit: 10, order: "desc" }),
                fetchStoriesForEditor({ isFlagged: true, limit: 10, order: "desc" })
            ]);

            setStories({ contracted, userStories: userWritten, flagged });
        } catch (error) {
            console.error("Error fetching stories:", error);
        } finally {
            setLoadingStories(false);
        }
    }, []);

    useEffect(() => {
        if (loading || !user || !isEditor) return; // Wait for auth state to resolve
        fetchStories();
    }, [loading, user, isEditor, fetchStories]); // 🔹 `fetchStories` is stable due to `useCallback`

    if (loading || loadingStories) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>Editorial Dashboard</Typography>

            <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/editorial/newstory")}
                sx={{ mb: 2, py: 2 }}
            >
                + Write a New Story
            </Button>

            <Divider sx={{ my: 2 }} />
            <StorySection title="Contracted Author Stories" stories={stories.contracted} filter="contracted" navigate={navigate} />
            <Divider sx={{ my: 2 }} />
            <StorySection title="User-Submitted Stories" stories={stories.userStories} filter="user" navigate={navigate} />
            <Divider sx={{ my: 2 }} />
            <StorySection title="Flagged Stories" stories={stories.flagged} filter="flagged" navigate={navigate} />
        </Box>
    );
};

const StorySection = ({ title, stories, filter, navigate }) => {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            <Box sx={{ mt: 2, display: "flex", overflowX: "auto", pb: 4, pt: 2, px: 2, gap: 2 }}>
                {stories.map((story) => (
                    <Box key={story.id} sx={{ flex: "0 0 auto", minWidth: 140 }}>
                        <StoryCard story={story} />
                    </Box>
                ))}
            </Box>
            <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate(`/editorial/stories?filter=${filter}`)}>
                See More
            </Button>
        </Box>
    );
};

export default EditorialPage;
